<template>
  <b-container class="mt-4" fluid>
    <b-form-checkbox
      v-if="!usingSalesforce"
      v-model="pagination"
      class="mb-4"
      name="pagination-toggle"
      switch
      unchecked-value="laravel"
      value="vue"
      >Vue Pagination
    </b-form-checkbox>
    <b-table
      ref="events_table"
      :busy.sync="loading"
      :current-page="!isUsingVuePagination ? undefined : currentPage"
      :fields="fields"
      :items="events"
      :per-page="!isUsingVuePagination ? undefined : perPage"
      head-variant="dark"
      hover
      responsive
      show-empty
      small
      stacked="sm"
    >
      <template #table-busy>
        <div class="text-center text-secondary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
      <template #cell(scheduled)="data">
        {{ data.value | yesNo }}
      </template>
      <template #table-caption>
        <div style="display: flex; justify-content: space-between">
          <p v-if="isUsingVuePagination">
            {{ showingMessage }}
          </p>
          <p v-else style="font-size: 16px">
            Showing {{ from }} to {{ to }} of {{ rows }}
            {{ currentlyShowing === 1 ? 'event' : 'events' }}
          </p>

          <b-pagination
            v-if="currentlyShowing > 0"
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="rows"
            align="center"
            aria-controls="events_table"
            pills
          ></b-pagination>
        </div>
      </template>
      <template #cell(actions)="row">
        <b-button variant="link" @click="row['toggleDetails']">
          <b-icon v-if="!row.detailsShowing" icon="eye-fill"></b-icon>
          <b-icon v-if="row.detailsShowing" icon="eye-slash-fill"></b-icon>
        </b-button>
      </template>
      <template #row-details="row">
        <b-card>
          <p>{{ row.item.description }}</p>
        </b-card>
      </template>
    </b-table>
  </b-container>
</template>

<script>
  import moment from 'moment/moment.js';

  export default {
    name: 'MissionControlEvents',
    data() {
      return {
        loading: false,
        pagination: 'vue',
        isUsingVuePagination: false,
        data: {},
        to: 0,
        from: 0,
        events: [],
        currentlyShowing: 0,
        currentPage: 1,
        rows: 0,
        perPage: 15,
        fields: [
          // {
          //   key: 'event_scope_id',
          //   label: 'Event Scope ID',
          //   // sortable: true,
          // },
          // {
          //   key: 'study',
          //   label: 'Study ID',
          //   sortable: true,
          // },
          // {
          //   key: 'siteNumber',
          //   label: 'Site #',
          //   sortable: true,
          // },
          {
            key: 'scope',
            label: 'Scope',
            sortable: true,
          },
          {
            key: 'name',
            label: 'Name',
            sortable: true,
          },
          {
            key: 'application',
            label: 'Application',
            sortable: true,
          },
          {
            key: 'businessImpact',
            label: 'Business Impact',
            sortable: true,
          },
          {
            key: 'severity',
            label: 'Severity',
            sortable: true,
          },
          {
            key: 'type',
            label: 'Type',
            sortable: true,
          },
          {
            key: 'status',
            label: 'Status',
            sortable: true,
          },
          {
            key: 'eventStartDate',
            label: 'Start Date',
            sortable: true,
            formatter: 'formatDate',
          },
          {
            key: 'eventEndDate',
            label: 'End Date',
            sortable: true,
            formatter: 'formatDate',
          },
          {
            key: 'scheduled',
            label: 'Scheduled',
            sortable: true,
          },
          {
            key: 'actions',
            label: 'Details',
          },
        ],
      };
    },
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' System Outage Messages';
      return {
        title: metaTitle,
      };
    },
    computed: {
      usingSalesforce() {
        return window['Laravel']['feature_flags']['use_salesforce_system_outage_messages'];
      },
      studyId() {
        return this.$route.params['studyId'];
      },
      siteId() {
        return this.$route.params['siteId'];
      },
      studySites() {
        return this.$route.params['studyId'] + ':' + this.$route.params['siteId'];
      },
      params() {
        return {
          params: {
            page: this.currentPage,
            studySites: this.studySites,
            pagination: this.pagination,
          },
        };
      },
      showingMessage() {
        let to = this.currentPage * 15 >= this.rows ? this.rows : this.currentPage * 15;
        return `Showing ${this.currentPage * 15 - 14} - ${to} of ${this.rows} events`;
      },
    },
    created() {
      this.pagination = this.$route.query['pagination'];
      this.getEvents();
    },
    watch: {
      $route() {
        this.getEvents();
      },
      currentPage() {
        if (!this.isUsingVuePagination) {
          this.getEvents();
        }
      },
      pagination(newValue) {
        this.getEvents();
      },
    },
    methods: {
      getEvents() {
        this.loading = true;
        this.reset();
        let url = `/api/system-outage-messages`;
        let params = this.params;
        if (this.usingSalesforce) {
          url = `/api/get-system-outage-messages`;
          params = {
            params: {
              study: this.studyId,
              site: this.siteId,
            },
          };
        }

        axios
          .get(url, params)
          .then(results => {
            if (this.usingSalesforce) {
              this.data = results.data.items;
            } else {
              this.data = results.data;
            }
            if ('current_page' in this.data) {
              this.setupLaravelPagination(this.data);
            } else {
              this.setupVuePagination(this.data);
            }
            this.loading = false;
          })
          .catch(error => {
            console.log(error);
            let errorMessage = 'Error getting data';
            if (error.response.status === 403) {
              errorMessage = 'You do not have access to retrieve system outage messages.';
            }
            this.$bvToast.toast(errorMessage, {
              title: 'Error',
              variant: 'danger',
              solid: true,
            });
            this.loading = false;
          });
      },
      setupLaravelPagination(laravel) {
        this.isUsingVuePagination = false;
        this.events = laravel.data;
        this.currentPage = laravel['current_page'];
        this.rows = laravel.total;
        this.perPage = laravel['per_page'];
        this.to = laravel['to'];
        this.from = laravel['from'];
        this.currentlyShowing = this.events.length;
      },
      setupVuePagination(vue) {
        this.isUsingVuePagination = true;
        this.events = vue;
        this.currentPage = 1;
        this.rows = vue.length;
        this.perPage = 15;
        this.currentlyShowing = this.events.length;
      },
      reset() {
        this.events = [];
        this.currentlyShowing = 0;
        this.currentPage = 1;
        this.to = 0;
        this.from = 0;
      },
      formatDate(date) {
        if (date == null || date === '') {
          return '';
        } else {
          return moment(date).format('DD MMM YYYY h:mm A');
        }
      },
    },
  };
</script>
